// 📄 components/StepCCSubaccount.tsx
import React, { useState, useEffect } from 'react';
import { Copy, Check, ExternalLink, Info, Globe2, ChevronDown, CheckCircle2, Circle } from 'lucide-react';
import { GeneralsFormData } from '../../../../../services/commerce/integrationService.ts';

interface StepCCSubaccountProps {
  ccBtpRegion: string;
  ccLocationId: string;
  ccDisplayName: string;
  ccSelectedSubaccountId: string;
  ccCompletedSubaccounts: string[];
  selectedEnv: 'DEV' | 'QAS' | 'PRD';
  onChange: (field: keyof GeneralsFormData, value: any) => void;
  isInvalid: boolean;
}

const SUBACCOUNT_USER = '$SAP-CP-SSO-PASSCODE$';
const SUGGESTED_LOCATION_ID = 'onehub_CIG';
const SUGGESTED_DISPLAY_NAME = 'Ariba CIG 1';

interface SubaccountEntry {
  id: string;
  passcodeUrl: string;
}

// Catálogo fijo de subcuentas BTP disponibles por región y grupo de ambiente
const BTP_REGIONS: Record<string, {
  label: string;
  dataCenter: string;
  test: SubaccountEntry[];
  prod: SubaccountEntry[];
}> = {
  EU: {
    label: 'Europa (EU)',
    dataCenter: 'cf.eu10.hana.ondemand.com',
    test: [
      { id: 'c2cd9a88-7143-472a-a358-ac050eacdc39', passcodeUrl: 'https://aribaeuprodt1.authentication.eu10.hana.ondemand.com/passcode' },
      { id: '2fdff90d-496d-4c6d-9f21-26d1bb9f37b5', passcodeUrl: 'https://aribaeuprodt2.authentication.eu10.hana.ondemand.com/passcode' },
      { id: 'c7845fda-254d-42ef-9069-70bde1d2b647', passcodeUrl: 'https://aribaeuprodt3.authentication.eu10.hana.ondemand.com/passcode' },
      { id: 'a4eca8c4-328d-492a-8ee0-cfb14a5d751c', passcodeUrl: 'https://cig-production-eu10-outbound-test.authentication.eu10.hana.ondemand.com/passcode' },
    ],
    prod: [
      { id: '38ed7fb3-5d18-4ca9-810f-751c5ce2b8ee', passcodeUrl: 'https://aribaeuprodp1.authentication.eu10.hana.ondemand.com/passcode' },
      { id: '0a5e6610-0c7d-4e7c-bc16-5181277b7405', passcodeUrl: 'https://aribaeuprodp2.authentication.eu10.hana.ondemand.com/passcode' },
      { id: '6b368bbf-6761-427c-91bd-158a79c2ab0d', passcodeUrl: 'https://aribaeuprodp3.authentication.eu10.hana.ondemand.com/passcode' },
      { id: 'c6938ed8-be1b-4bb3-b140-1bb8c99d76ef', passcodeUrl: 'https://aribaeuprodp4.authentication.eu10.hana.ondemand.com/passcode' },
      { id: 'eddaa307-3566-451f-8d09-57d27dd76985', passcodeUrl: 'https://aribaeuprodp5.authentication.eu10.hana.ondemand.com/passcode' },
      { id: '86efbbb1-a71b-42ca-900d-b7d24be2234e', passcodeUrl: 'https://aribaeuprodp6.authentication.eu10.hana.ondemand.com/passcode' },
      { id: 'c4fdd25c-03cf-480d-9024-082641a58e5a', passcodeUrl: 'https://aribaeuprodp7.authentication.eu10.hana.ondemand.com/passcode' },
      { id: '181236c4-d33b-4948-9c14-87d3b4a3b7b5', passcodeUrl: 'https://cig-production-eu10-outbound-prod.authentication.eu10.hana.ondemand.com/passcode' },
    ]
  },
  NA: {
    label: 'USA (NA)',
    dataCenter: 'cf.us10.hana.ondemand.com',
    test: [
      { id: '865860f6-c079-4e00-bfb1-da6d4c202bda', passcodeUrl: 'https://aribausprodt1.authentication.us10.hana.ondemand.com/passcode' },
      { id: 'ca9e875f-3213-4277-89c7-48c0721fe1a8', passcodeUrl: 'https://aribausprodt2.authentication.us10.hana.ondemand.com/passcode' },
      { id: '9f78d636-ced8-441a-8543-23870a6c5353', passcodeUrl: 'https://cig-production-us10-outbound-test.authentication.us10.hana.ondemand.com/passcode' },
    ],
    prod: [
      { id: 'bd12b724-5a80-46dd-9834-c748486de411', passcodeUrl: 'https://aribausprodp1.authentication.us10.hana.ondemand.com/passcode' },
      { id: 'fa099043-3ff3-465a-90a5-808b3f324aac', passcodeUrl: 'https://aribausprodp2.authentication.us10.hana.ondemand.com/passcode' },
      { id: '4d527499-b34f-4f67-b046-7c5b561eebbb', passcodeUrl: 'https://aribausprodp3.authentication.us10.hana.ondemand.com/passcode' },
      { id: 'bc3debb0-9dea-468c-ab1d-010ca7f67158', passcodeUrl: 'https://aribausprodp4.authentication.us10.hana.ondemand.com/passcode' },
      { id: 'c1c8acd4-1921-4c81-a6a6-d04023fde368', passcodeUrl: 'https://aribausprodp5.authentication.us10.hana.ondemand.com/passcode' },
      { id: '80ec591d-5211-44c6-873d-c67456799567', passcodeUrl: 'https://aribausprodp6.authentication.us10.hana.ondemand.com/passcode' },
      { id: '4869e1a2-f119-4a1d-acd8-5211df9aa417', passcodeUrl: 'https://cig-production-us10-outbound-prod.authentication.us10.hana.ondemand.com/passcode' },
    ]
  }
};

export function StepCCSubaccount({
  ccBtpRegion,
  ccLocationId,
  ccDisplayName,
  ccSelectedSubaccountId,
  ccCompletedSubaccounts,
  selectedEnv,
  onChange,
  isInvalid
}: StepCCSubaccountProps) {
  const [copiedField, setCopiedField] = useState<'user' | 'region' | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const region = BTP_REGIONS[ccBtpRegion] || BTP_REGIONS.EU;
  const envGroup: 'test' | 'prod' = selectedEnv === 'PRD' ? 'prod' : 'test';
  const currentList = region[envGroup];
  const completedList = ccCompletedSubaccounts || [];

  const selectedSubaccount = currentList.find(s => s.id === ccSelectedSubaccountId);
  const completedCount = currentList.filter(s => completedList.includes(s.id)).length;

  // Si cambia el Data Center o el ambiente activo, la subcuenta seleccionada
  // puede dejar de pertenecer a la lista vigente: se resetea la selección.
  useEffect(() => {
    const list = (BTP_REGIONS[ccBtpRegion] || BTP_REGIONS.EU)[selectedEnv === 'PRD' ? 'prod' : 'test'];
    if (ccSelectedSubaccountId && !list.some(s => s.id === ccSelectedSubaccountId)) {
      onChange('ccSelectedSubaccountId', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ccBtpRegion, selectedEnv]);

  const sortedList = [...currentList].sort((a, b) => {
    const aDone = completedList.includes(a.id) ? 1 : 0;
    const bDone = completedList.includes(b.id) ? 1 : 0;
    return aDone - bDone;
  });

  const handleCopy = (text: string, field: 'user' | 'region') => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1500);
  };

  const handleSelectSubaccount = (id: string) => {
    onChange('ccSelectedSubaccountId', id);
    setDropdownOpen(false);
  };

  const handleToggleCompleted = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const isDone = completedList.includes(id);
    const next = isDone ? completedList.filter(x => x !== id) : [...completedList, id];
    onChange('ccCompletedSubaccounts', next);
  };

  return (
    <div className="space-y-4 animate-fadeIn">
      <div className="bg-indigo-500/5 border border-indigo-500/10 rounded-xl p-3 space-y-1">
        <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest block">SAP BTP Cockpit</span>
        <p className="font-mono text-xs text-gray-300">
          <span>Cloud Connector → Add Subaccount</span>
        </p>
      </div>

      <div>
        <h3 className="text-lg font-medium text-indigo-400">1. Add Subaccount (BTP Setup)</h3>
        <p className="text-sm text-gray-400 mt-1">
          SAP está migrando la plataforma a SAP BTP. Configura manualmente la subcuenta usando la región,
          el Subaccount ID y el código de autenticación temporal (Passcode) generado desde el Cockpit.
        </p>
      </div>

      <div className="bg-white/5 p-4 rounded-xl border border-white/5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Data Center <span className="text-red-400">*</span>
            </label>
            <select
              value={ccBtpRegion}
              onChange={(e) => onChange('ccBtpRegion', e.target.value)}
              className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500"
            >
              <option value="EU">Europa (EU)</option>
              <option value="NA">USA (NA)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Región Técnica</label>
            <div className="flex items-center gap-2 bg-zinc-800 border border-white/10 rounded-lg p-2.5">
              <span className="flex-1 font-mono text-xs text-gray-300 select-all truncate">{region.dataCenter}</span>
              <button
                type="button"
                onClick={() => handleCopy(region.dataCenter, 'region')}
                className="flex items-center gap-1 text-[10px] font-semibold text-indigo-400 hover:text-indigo-300 px-2 py-1 rounded-md hover:bg-indigo-500/10 transition-all flex-shrink-0"
              >
                {copiedField === 'region' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copiedField === 'region' ? 'Copiado' : 'Copiar'}
              </button>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">Subaccount User</label>
          <div className="flex items-center gap-2 bg-zinc-800 border border-white/10 rounded-lg p-2.5">
            <span className="flex-1 font-mono text-xs text-gray-300 select-all truncate">{SUBACCOUNT_USER}</span>
            <button
              type="button"
              onClick={() => handleCopy(SUBACCOUNT_USER, 'user')}
              className="flex items-center gap-1 text-[10px] font-semibold text-indigo-400 hover:text-indigo-300 px-2 py-1 rounded-md hover:bg-indigo-500/10 transition-all flex-shrink-0"
            >
              {copiedField === 'user' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copiedField === 'user' ? 'Copiado' : 'Copiar'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Location ID (sugerido) <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={ccLocationId}
              onChange={(e) => onChange('ccLocationId', e.target.value)}
              placeholder={SUGGESTED_LOCATION_ID}
              className={`w-full bg-zinc-800 border rounded-lg p-2.5 text-sm font-mono text-white focus:outline-none focus:border-indigo-500 ${
                isInvalid && !ccLocationId ? 'border-red-500' : 'border-white/10'
              }`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Display Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={ccDisplayName}
              onChange={(e) => onChange('ccDisplayName', e.target.value)}
              placeholder={SUGGESTED_DISPLAY_NAME}
              className={`w-full bg-zinc-800 border rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500 ${
                isInvalid && !ccDisplayName ? 'border-red-500' : 'border-white/10'
              }`}
            />
          </div>
        </div>
      </div>

      {/* Selector de Subaccount + checklist de completadas */}
      <div className="bg-white/5 p-4 rounded-xl border border-white/5 space-y-2">
        <div className="flex items-center justify-between">
          <label className="block text-xs font-medium text-gray-400">Subaccount</label>
          <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-wider">
            {completedCount} de {currentList.length} subcuentas configuradas
          </span>
        </div>

        <div className="relative">
          <button
            type="button"
            onClick={() => setDropdownOpen(o => !o)}
            className="w-full flex items-center justify-between gap-2 bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-left focus:outline-none focus:border-indigo-500"
          >
            <span className={`font-mono text-xs truncate ${selectedSubaccount ? 'text-white' : 'text-gray-500'}`}>
              {selectedSubaccount ? selectedSubaccount.id : 'Selecciona una subcuenta...'}
            </span>
            <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {dropdownOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setDropdownOpen(false)} />
              <div className="absolute z-20 mt-1 w-full bg-zinc-900 border border-white/10 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                {sortedList.map((s) => {
                  const isDone = completedList.includes(s.id);
                  const isSelected = s.id === ccSelectedSubaccountId;
                  return (
                    <div
                      key={s.id}
                      onClick={() => handleSelectSubaccount(s.id)}
                      className={`flex items-center justify-between gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-white/5 border-b border-white/5 last:border-b-0 ${isSelected ? 'bg-indigo-500/10' : ''}`}
                    >
                      <span className={`font-mono truncate ${isDone ? 'text-gray-500 line-through' : 'text-gray-200'}`}>
                        {s.id}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => handleToggleCompleted(s.id, e)}
                        title={isDone ? 'Marcar como pendiente' : 'Marcar como completada en Cloud Connector'}
                        className={`flex-shrink-0 p-1 rounded-md transition-all ${isDone ? 'text-green-400 hover:text-green-300' : 'text-gray-600 hover:text-gray-300'}`}
                      >
                        {isDone ? <CheckCircle2 className="w-4 h-4" fill="currentColor" /> : <Circle className="w-4 h-4" />}
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Tarjeta destacada: Subaccount ID + Passcode URL de la subcuenta seleccionada */}
      <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Globe2 className="w-4 h-4 text-indigo-400 flex-shrink-0" />
          <span className="text-xs font-semibold text-indigo-300 uppercase tracking-wider">
            Subcuenta activa · {region.label} · {selectedEnv} ({envGroup === 'prod' ? 'Productivo' : 'Test'})
          </span>
        </div>

        {selectedSubaccount ? (
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <label className="block text-[10px] text-gray-400 mb-1">Subaccount ID</label>
              <div className="bg-zinc-900/60 border border-white/10 rounded-lg p-2.5 font-mono text-xs text-white select-all truncate">
                {selectedSubaccount.id}
              </div>
            </div>
            <a
              href={selectedSubaccount.passcodeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-[#fff] font-medium text-xs px-4 py-2.5 rounded-lg shadow-md transition-all active:scale-[0.98] whitespace-nowrap"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Abrir Passcode URL
            </a>
          </div>
        ) : (
          <p className="text-xs text-indigo-300/70">Selecciona una subcuenta del listado para ver su Passcode URL.</p>
        )}
      </div>

      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 text-blue-300 flex gap-3 text-sm">
        <Info className="w-5 h-5 flex-shrink-0" />
        <div>
          <p className="font-semibold">¿Cómo obtener el Passcode?</p>
          <p className="text-xs text-blue-300/80 mt-0.5">
            Ingresa a la URL del Passcode con tu <strong>PUser corporativo</strong> para generar el código temporal
            alfanumérico de 25 caracteres, y pégalo en el campo <strong>Password</strong> del formulario "Add Subaccount"
            en el Cloud Connector local.
          </p>
        </div>
      </div>
    </div>
  );
}
